import uniq from 'lodash/uniq';

import { DeviceStatus } from 'chaika-enums';
import type { DeviceCreateForm, DeviceData, DeviceUpdateForm } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { DeviceApi } from '@organization/api/DeviceApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';

class DeviceService {
    private isLoaded = false;
    public async load() {
        if (!this.isLoaded) {
            const deviceList = store.deviceList;
            const devices = await DeviceApi.getList();

            const organizationIds = uniq(devices.map(device => device.organizationId));
            organizationIds.forEach(organizationId => {
                deviceList.addLoadedOrganizationId(organizationId);
            });
            deviceList.add(devices);
            this.isLoaded = true;
        }
    }

    public async loadByIds(ids: string[]) {
        const deviceList = store.deviceList;

        const newIds = getUniqNewIds(deviceList.ids, ids);

        if (newIds.length > 0) {
            const devices = await DeviceApi.getList({ ids: newIds.join(',') });

            deviceList.add(devices);
        }
    }

    public async loadByOrganizationId(organizationId: string) {
        const deviceList = store.deviceList;

        if (!deviceList.isOrganizationDevicesLoaded(organizationId)) {
            const devices = await DeviceApi.getList({ organizationId });
            deviceList.add(devices);
            deviceList.addLoadedOrganizationId(organizationId);
        }
    }

    public async create(device: DeviceCreateForm) {
        const deviceList = store.deviceList;

        await DeviceApi.create(device);
        deviceList.add([{ status: DeviceStatus.Active, ...device } as DeviceData]);
    }

    public async updateDevice(deviceId: string, params: DeviceUpdateForm) {
        await DeviceApi.update(deviceId, params);

        const device = store.deviceList.getExisting(deviceId);

        store.deviceList.update(deviceId, { ...device, ...params });
    }

    public async toggleDeviceStatus(deviceId: string) {
        const device = store.deviceList.getExisting(deviceId);
        const newStatus = device.status === DeviceStatus.Active ? DeviceStatus.Archived : DeviceStatus.Active;

        await DeviceApi.updateStatus(deviceId, newStatus);
        store.deviceList.update(deviceId, { ...device, status: newStatus });
    }
}

export const deviceService = new DeviceService();
